import React, { createContext, useContext, useState, useEffect } from 'react';

const FeedbackContext = createContext();

// Generate unique ID
const generateId = () => `fb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

export function FeedbackProvider({ children }) {
  const [feedbackItems, setFeedbackItems] = useState(() => {
    const saved = localStorage.getItem('acusightpro_feedback');
    return saved ? JSON.parse(saved) : [];
  });

  const [suggestions, setSuggestions] = useState(() => {
    const saved = localStorage.getItem('acusightpro_suggestions');
    return saved ? JSON.parse(saved) : [];
  });

  // Persist to localStorage
  useEffect(() => {
    localStorage.setItem('acusightpro_feedback', JSON.stringify(feedbackItems));
  }, [feedbackItems]);

  useEffect(() => {
    localStorage.setItem('acusightpro_suggestions', JSON.stringify(suggestions));
  }, [suggestions]);

  // Add feedback (thumbs up/down)
  const addFeedback = (feedback) => {
    const newFeedback = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      ...feedback,
      // feedback should include: feature, module, userInput, aiOutput, rating ('up' or 'down'), 
      // issueType (if down), comment (optional)
    };
    setFeedbackItems(prev => [newFeedback, ...prev]);
    return newFeedback.id;
  };

  // Add suggestion
  const addSuggestion = (suggestion) => {
    const newSuggestion = {
      id: generateId(),
      timestamp: new Date().toISOString(),
      status: 'new', // new, reviewed, implemented, declined
      ...suggestion,
      // suggestion should include: title, description, category
    };
    setSuggestions(prev => [newSuggestion, ...prev]);
    return newSuggestion.id;
  };

  // Get feedback stats
  const getStats = () => {
    const total = feedbackItems.length;
    const thumbsUp = feedbackItems.filter(f => f.rating === 'up').length;
    const thumbsDown = feedbackItems.filter(f => f.rating === 'down').length;
    
    // Issue type breakdown
    const issueTypes = feedbackItems
      .filter(f => f.rating === 'down' && f.issueType)
      .reduce((acc, f) => {
        acc[f.issueType] = (acc[f.issueType] || 0) + 1;
        return acc;
      }, {});

    // Feature breakdown
    const byFeature = feedbackItems.reduce((acc, f) => {
      if (!acc[f.feature]) {
        acc[f.feature] = { up: 0, down: 0 };
      }
      acc[f.feature][f.rating]++;
      return acc;
    }, {});

    return {
      total,
      thumbsUp,
      thumbsDown,
      satisfactionRate: total > 0 ? ((thumbsUp / total) * 100).toFixed(1) : 0,
      issueTypes,
      byFeature,
      suggestionCount: suggestions.length,
    };
  };

  // Clear all feedback (for demo reset)
  const clearAllFeedback = () => {
    setFeedbackItems([]);
    setSuggestions([]);
    localStorage.removeItem('acusightpro_feedback');
    localStorage.removeItem('acusightpro_suggestions');
  };

  return (
    <FeedbackContext.Provider value={{
      feedbackItems,
      suggestions,
      addFeedback,
      addSuggestion,
      getStats,
      clearAllFeedback,
    }}>
      {children}
    </FeedbackContext.Provider>
  );
}

export function useFeedback() {
  const context = useContext(FeedbackContext);
  if (!context) {
    throw new Error('useFeedback must be used within a FeedbackProvider');
  }
  return context;
}

export default FeedbackContext;
